import { inject, Injectable, signal } from '@angular/core';
import {
  type DesignerContext,
  type ImageSummarizerInput,
  type ImageSummarizerOutput,
  imageSummarizerOutputSchema,
  isSupportedImageSummarizerMimeType,
  mapDiagramPayloadToDesignerContext,
  MAX_IMAGE_SUMMARIZER_BASE64_BYTES,
} from '@bizzdesign/ai-assistant/workflows';
import {
  assertIsUUID,
  generateUUID,
  isUUID,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type BasicData,
  type DiagramPayload,
  type DraftData,
  type GenericObject,
  type ImageClassification,
  type ImageClassificationFields,
  type ImageDeliverable,
  type ImageDeliverableTextMetadata,
  type ModelerData,
  type NeighboursDefinition,
  NoViewDataError,
  type PayloadDraftEntry,
  type PublishError,
  type PublishViewPayload,
  relationRoleMap,
  type Result,
  SupportedMetamodels,
  type ViewData,
  type ViewSummary,
} from '@bizzdesign/modeler/models';
import {
  detectMimeTypeFromBase64,
  toImageClassificationFields,
} from '@bizzdesign/modeler/utils-canvazz';
import {
  convertBaseRelationToSemanticalRelationData,
  convertFromSDKNeighborData,
  convertFromSDKPublishedViewData,
  convertObjectToSemanticalData,
  getAllPagesData,
  getAllStreamData,
  mapFromRepoPairsToApiIds,
} from '@bizzdesign/modeler/utils-modeler';
import {
  type CreateDeliverablePayload,
  type DeleteDeliverableRelationshipPayload,
  type Deliverable,
  DeliverableClient,
  type DeliverableFilter,
  type DeliverableRelationship,
} from '@bizzdesign/sdk-deliverable';
import type {
  BaseRelationDto,
  Combined,
  MetaModel,
  ObjectDtoSelector,
  RelationDtoSelector,
} from '@bizzdesign/sdk-knowledgegraph';
import { BDCoreMetaModelDefinition } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import {
  type ObjectSearchResult,
  SearchClient,
  type SearchObjectsParameters,
} from '@bizzdesign/sdk-search';
import {
  isWorkflowErrorPayload,
  WorkflowClient,
} from '@bizzdesign/sdk-workflow';
import type {
  BaseObjectDto,
  DeletedElementsState,
  ObjectDto,
  ObjectSearchResultDto,
  RelationDto,
  RepoLoadedElementDataResponse,
  RepositoryLoadedElementData,
  RepositoryLoadedElementIdPair,
} from '@bizzdesign/shared/models';
import { KnowledgeGraphClientBDCore } from '@bizzdesign/shared/services';
import { rasterizeSvgToPngDataUrl } from '@bizzdesign/shared/utils';
import { isEqual } from 'lodash-es';
import {
  BehaviorSubject,
  bufferCount,
  catchError,
  defaultIfEmpty,
  firstValueFrom,
  forkJoin,
  from,
  map,
  type Observable,
  of,
  switchMap,
  throwError,
  toArray,
  zipWith,
} from 'rxjs';
import { z } from 'zod/v4';

export const GET_OBJECTS_PAGE_SIZE = 50;
export const VIEW_SELECT_PAGE_SIZE = 25;
export const MOCK_EXTERNAL_SOURCE = 'Pandora-Test';

const IMAGE_DELIVERABLE_PREVIEW_VERSION = 'image.preview.1';
const IMAGE_SUMMARIZER_ENDPOINT_PATH = '/suggestions/image-summarizer';

// Accepts the legacy `{ isDiagram }` object: a parse failure reads as "no saved
// metadata", which would discard descriptions already on disk.
const storedImageClassificationSchema = z.union([
  z.enum(['diagram', 'other']),
  z
    .object({ isDiagram: z.boolean() })
    .transform(
      ({ isDiagram }): ImageClassification => (isDiagram ? 'diagram' : 'other'),
    ),
]);

const storedImageDeliverableMetadataSchema = z.object({
  description: z.string(),
  alt: z.string(),
  classification: storedImageClassificationSchema.optional(),
  mermaid: z.string().optional(),
});

const imageDeliverablePreviewSchema = z.object({
  version: z.string(),
  payload: storedImageDeliverableMetadataSchema,
});

type ImageDeliverablePreview = {
  readonly version: typeof IMAGE_DELIVERABLE_PREVIEW_VERSION;
  readonly payload: ImageDeliverableTextMetadata;
};

type CreatedImageDeliverable = {
  readonly id: UUID;
  readonly name: string;
  readonly description?: string;
  readonly alt?: string;
} & ImageClassificationFields;

type GetImageDeliverablesOptions = {
  readonly forceRegeneration?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ModelerDataService {
  private readonly PAGE_SIZE = 20;
  private readonly nextPage$ = new BehaviorSubject<void>(undefined);
  private readonly knowledgeGraphClient = inject(KnowledgeGraphClientBDCore);
  private readonly searchClient = inject(SearchClient);
  private readonly deliverableClient = inject(DeliverableClient);
  private readonly workflowClient = inject(WorkflowClient, { optional: true });
  relationRoleMap: Map<string, Combined.RelationRoleName>;
  readonly modelerData = signal<ModelerData | null>(null);

  constructor() {
    this.relationRoleMap = relationRoleMap;
  }

  getViewData(
    viewId: string,
    convertToFreeformat = false,
  ): Observable<ViewData | NoViewDataError> {
    assertIsUUID(viewId);
    return from(
      this.knowledgeGraphClient.getView({
        filter: {
          id: viewId,
        },
      }),
    ).pipe(
      map((viewData) => {
        if (viewData.some) {
          const payload = convertFromSDKPublishedViewData(
            viewData.val,
            convertToFreeformat,
          );
          const description = viewData.val.description ?? undefined;
          const deliverableIds = viewData.val.referencedDeliverableIds ?? [];
          return {
            name: viewData.val.name ?? '',
            ...(description === undefined ? {} : { description }),
            ...(deliverableIds.length > 0
              ? { referencedDeliverableIds: deliverableIds }
              : {}),
            payload,
          };
        }

        return new NoViewDataError(viewId);
      }),
    );
  }

  getViewDescription(viewId: string): Observable<string | undefined> {
    assertIsUUID(viewId);
    return from(
      this.knowledgeGraphClient.getView({
        filter: {
          id: viewId,
        },
        selector: {
          description: true,
        },
      }),
    ).pipe(
      map((viewData) =>
        viewData.some ? (viewData.val.description ?? undefined) : undefined,
      ),
      catchError((error) => {
        throw error;
      }),
    );
  }

  searchViews(
    searchTerm: string,
    pageSize: number,
    pageIndex: number,
  ): Observable<{ views: ViewSummary[]; totalCount: number }> {
    const result = this.knowledgeGraphClient.getViews({
      filter: { searchTerm },
      selector: {
        description: true,
      },
    });
    const pages = result.asPages({ pageSize });

    return forkJoin({
      items: from(pages.getPage(pageIndex)),
      totalCount: from(result.getCount()),
    }).pipe(
      map(({ items, totalCount }) => ({
        views: items.map(({ id, name, description }) => ({
          id,
          name: name ?? '',
          ...(description === null || description === undefined
            ? {}
            : { description }),
        })),
        totalCount,
      })),
      catchError((error) => {
        throw error;
      }),
    );
  }

  publishView(
    payload: PublishViewPayload,
    republish: boolean,
  ): Promise<Result<void, PublishError[]>> {
    return this.knowledgeGraphClient.publishView(payload, {
      republish: republish,
    });
  }

  async getObjectsByIds(ids: readonly UUID[]): Promise<ObjectDto[]> {
    if (ids.length === 0) {
      return [];
    }
    const objectCall = this.knowledgeGraphClient.getObjects({
      filter: {
        ids: [...ids],
      },
    });
    const objects = await getAllPagesData<ObjectDto>(
      objectCall,
      GET_OBJECTS_PAGE_SIZE,
    );
    return objects;
  }

  async getRelationsByIds(ids: readonly UUID[]): Promise<RelationDto[]> {
    if (ids.length === 0) {
      return [];
    }
    const relationsCall = this.knowledgeGraphClient.getRelations({
      filter: {
        relationIds: [...ids],
      },
    });

    const relations = await getAllPagesData<RelationDto>(
      relationsCall,
      GET_OBJECTS_PAGE_SIZE,
    );
    return relations;
  }

  getDiagramPayloadForObjects(searchObjects: ObjectDto[]): DiagramPayload {
    const semanticElements = searchObjects.map((object) =>
      convertObjectToSemanticalData(object),
    );
    const payload: PayloadDraftEntry = semanticElements.reduce(
      (accumulator, element) => {
        accumulator[element.id] = { ...element } as BasicData;
        return accumulator;
      },
      {} as PayloadDraftEntry,
    );

    return {
      payload,
      stateToken: '',
      fromRepoElements: semanticElements.map((element) => element.id),
      metamodelMetadata: {
        currentMetamodelName: SupportedMetamodels.FREEFORMAT,
        targetMetamodelName: undefined,
        convertFromPartialTargetMetamodel: true,
      },
    };
  }

  async isRelationNameUnique(
    relationName: string,
    type: string,
    sourceObjectId: string,
    targetObjectId: string,
  ) {
    const relationRoleName = this.convertTypeNameToRelationType(type);
    BDCoreMetaModelDefinition.assertIsRelationRoleName(relationRoleName);
    assertIsUUID(sourceObjectId);
    assertIsUUID(targetObjectId);
    const relations = this.knowledgeGraphClient.getNeighboringRelationsOfObject(
      {
        filter: {
          sourceObjectId,
          targetObjectId,
          relationName,
          relationRoleName,
        },
      },
    );
    const stream = relations.stream();
    const { value: firstRelation, done } = await stream.next();
    return done || !firstRelation
      ? null
      : convertBaseRelationToSemanticalRelationData(firstRelation);
  }

  searchInRepo$(
    searchTerm: string,
    metamodelScope: MetaModel[],
  ): Observable<ObjectSearchResultDto[]> {
    const params: SearchObjectsParameters = {
      searchTerm: searchTerm,
      metaModel: metamodelScope,
      scopes: ['OBJECT'],
    };
    const result = this.searchClient.search(params);
    return from(result.results).pipe(
      // buffer results per page
      bufferCount(this.PAGE_SIZE),
      // flush page per next event
      zipWith(this.nextPage$),
      // stream results page by page
      map(([searchResults]) =>
        searchResults.filter(
          (result): result is ObjectSearchResult => result.scope === 'OBJECT',
        ),
      ),
      defaultIfEmpty([]),
    );
  }

  async getNeighbours(elementId: string): Promise<NeighboursDefinition> {
    assertIsUUID(elementId);
    const neighborCall =
      this.knowledgeGraphClient.getNeighboringRelationsOfObject({
        filter: {
          sourceObjectId: elementId,
          directionHandling: 'normalized',
        },
      });
    const neighboursResponse: BaseRelationDto[] =
      await getAllStreamData<BaseRelationDto>(neighborCall);

    const neighbors = convertFromSDKNeighborData(neighboursResponse, elementId);
    return neighbors;
  }

  convertGenericObjectsToElementIds(objects: GenericObject[]): UUID[] {
    return (
      objects
        .map((obj) => obj['semanticsId'] ?? obj['promotion']?.['semanticsId'])
        .filter((id) => !!id) ?? []
    );
  }

  convertModelDataToPayloadDraftEntry(
    modelData: GenericObject[],
  ): PayloadDraftEntry {
    const payload: GenericObject = {};
    for (const objectDetails of modelData) {
      if (objectDetails) {
        // If only the id is in the objectDetails,
        // then treat it as a null (to indicate a deleted object)
        payload[objectDetails['id']] =
          Object.keys(objectDetails).length === 1 ? null : objectDetails;
      }
    }

    // we remove null that indicates deleted objects because the current save impl will overwrite the stored object anyway
    return {
      ...Object.fromEntries(
        Object.entries<DraftData>({
          ...payload,
        }).filter(([_, value]) => value != null),
      ),
    };
  }

  convertTypeNameToRelationType(typeName: string): Combined.RelationRoleName {
    const relationType = this.relationRoleMap.get(typeName);
    if (!relationType)
      throw new Error('Relation Type has no mapped version for SDK!');

    return relationType;
  }

  async deleteObjects(objectIds: UUID[]) {
    try {
      const result = await this.knowledgeGraphClient.deleteObjects({
        ids: objectIds,
      });
      if (result.ok) {
        console.log('Objects deleted successfully', objectIds);
        return result.ok;
      }
      throw new Error('Failed to delete objects');
    } catch (error) {
      console.error('Failed to delete objects', error, objectIds);
      throw new Error('Failed to delete objects');
    }
  }

  async deleteRelations(relationIds: UUID[]) {
    try {
      const result = await this.knowledgeGraphClient.deleteRelations({
        ids: relationIds,
      });
      if (result.ok) {
        console.log('Relations deleted successfully', relationIds);
        return result.ok;
      }
      throw new Error('Failed to delete relations');
    } catch (error) {
      console.error('Failed to delete relations', error, relationIds);
      throw new Error('Failed to delete relations');
    }
  }

  buildDesignerContext(
    frameId: UUID | undefined,
    frameName: string,
    diagramPayload: DiagramPayload,
    imageMetadata:
      | ReadonlyMap<string, ImageDeliverableTextMetadata>
      | undefined,
  ): Promise<DesignerContext> {
    const fetchPublishedObjects = async (ids: UUID[]) => {
      const objectResult = this.knowledgeGraphClient.getObjects({
        filter: { ids },
      });
      const allObjects: ObjectDto[] = await getAllPagesData<ObjectDto>(
        objectResult,
        GET_OBJECTS_PAGE_SIZE,
      );
      return allObjects;
    };

    return mapDiagramPayloadToDesignerContext(
      frameId,
      frameName,
      diagramPayload,
      fetchPublishedObjects,
      imageMetadata,
    );
  }

  async getDeletedObjects(
    fromRepoIds: RepositoryLoadedElementIdPair[],
  ): Promise<DeletedElementsState> {
    if (fromRepoIds.length === 0) {
      return {
        ids: [],
        error: null,
      };
    }
    try {
      const objectPages = this.knowledgeGraphClient
        .getObjects({
          filter: {
            ids: mapFromRepoPairsToApiIds(fromRepoIds),
          },
          selector: {} as const satisfies ObjectDtoSelector,
        })
        .asPages({ pageSize: GET_OBJECTS_PAGE_SIZE });
      const relationPages = this.knowledgeGraphClient
        .getRelations({
          filter: {
            relationIds: mapFromRepoPairsToApiIds(fromRepoIds),
          },
          selector: {} as const satisfies RelationDtoSelector,
        })
        .asPages({ pageSize: GET_OBJECTS_PAGE_SIZE });
      const arrayOfObservables: Array<Observable<UUID[]>> = [];

      const [numberOfObjectPages, numberOfRelationPages] = await Promise.all([
        objectPages.getNumberOfPages(),
        relationPages.getNumberOfPages(),
      ]);

      for (let i = 0; i < numberOfObjectPages; i++) {
        const objectPage$ = from(objectPages.getPage(i)).pipe(
          map((objects) => objects.map((obj) => obj.id)),
        );
        arrayOfObservables.push(objectPage$);
      }
      for (let i = 0; i < numberOfRelationPages; i++) {
        const relationPage$ = from(relationPages.getPage(i)).pipe(
          map((relations) => relations.map((rel) => rel.id)),
        );
        arrayOfObservables.push(relationPage$);
      }
      if (arrayOfObservables.length === 0) {
        return {
          ids: fromRepoIds,
          error: null,
        };
      }
      const result = await firstValueFrom(forkJoin(arrayOfObservables));
      return {
        ids: fromRepoIds.filter(
          (idPair) =>
            !result.some((obj) => obj.includes(idPair.semanticId as UUID)),
        ),
        error: null,
      };
    } catch (error) {
      console.error('Failed to fetch deleted objects', error);
      return {
        ids: undefined,
        error: new Error('Failed to fetch deleted objects'),
      };
    }
  }

  async getLatestDataForFromRepoElements(
    fromRepoElements: RepositoryLoadedElementData[],
  ): Promise<{
    data: RepositoryLoadedElementData[];
    error: Error | null;
  }> {
    if (fromRepoElements.length === 0) {
      return {
        data: [],
        error: null,
      };
    }
    try {
      const objectPages = this.knowledgeGraphClient
        .getObjects({
          filter: {
            ids: mapFromRepoPairsToApiIds(fromRepoElements),
          },
          selector: {
            externalId: true,
            externalSource: true,
          },
        })
        .asPages({ pageSize: GET_OBJECTS_PAGE_SIZE });
      const relationPages = this.knowledgeGraphClient
        .getRelations({
          filter: {
            relationIds: mapFromRepoPairsToApiIds(fromRepoElements),
          },
          selector: {
            externalId: true,
            externalSource: true,
          },
        })
        .asPages({ pageSize: GET_OBJECTS_PAGE_SIZE });
      const arrayOfObservables: Array<
        Observable<RepoLoadedElementDataResponse[]>
      > = [];

      const [numberOfObjectPages, numberOfRelationPages] = await Promise.all([
        objectPages.getNumberOfPages(),
        relationPages.getNumberOfPages(),
      ]);

      for (let i = 0; i < numberOfObjectPages; i++) {
        const objectPage$ = from(objectPages.getPage(i)).pipe(
          map((objects) =>
            objects.map<RepoLoadedElementDataResponse>((obj) => ({
              semanticId: obj.id,
              name: obj.name,
              externalData:
                obj.externalSource && obj.externalId
                  ? { id: obj.externalId, source: obj.externalSource }
                  : undefined,
            })),
          ),
        );
        arrayOfObservables.push(objectPage$);
      }
      for (let i = 0; i < numberOfRelationPages; i++) {
        const relationPage$ = from(relationPages.getPage(i)).pipe(
          map((relations) =>
            relations.map<RepoLoadedElementDataResponse>((rel) => ({
              semanticId: rel.id,
              name: rel.name ?? undefined,
              externalData:
                rel.externalSource && rel.externalId
                  ? { id: rel.externalId, source: rel.externalSource }
                  : undefined,
            })),
          ),
        );
        arrayOfObservables.push(relationPage$);
      }
      if (arrayOfObservables.length === 0) {
        return {
          // don't return any changed elements
          data: [],
          error: null,
        };
      }
      const result = await firstValueFrom(forkJoin(arrayOfObservables));
      const returnData = this.checkValuesForChanges(fromRepoElements, result);
      return {
        data: returnData,
        error: null,
      };
    } catch (error) {
      console.error('Failed to fetch latest data of from repo', error);
      return {
        data: [],
        error: new Error('Failed to fetch latest data of from repo elements'),
      };
    }
  }

  checkValuesForChanges(
    loadedFromRepoElements: RepositoryLoadedElementData[],
    apiResult: RepoLoadedElementDataResponse[][],
  ): RepositoryLoadedElementData[] {
    const flattenedAPiResult = apiResult.flat(2);
    let changedElements: RepositoryLoadedElementData[] = [];
    for (const apiElem of flattenedAPiResult) {
      const { semanticId, name, externalData } = apiElem;
      const loadedElement = loadedFromRepoElements.filter(
        (elem) => elem.semanticId === semanticId,
      );
      if (!loadedElement.length) continue;
      // keeping this case sensitive
      if (
        loadedElement.every(
          (el) => el.name === name && isEqual(externalData, el.externalData),
        )
      )
        continue;

      const updatedSubItems = loadedElement.map((el) => ({
        semanticId,
        graphicId: el.graphicId,
        name: name ?? el.name,
        externalData: externalData,
      }));
      changedElements.push(...updatedSubItems);
    }
    return changedElements;
  }

  async updateObject(id: UUID): Promise<BaseObjectDto | undefined> {
    const newExternalId = generateUUID();
    const result = await this.knowledgeGraphClient.updateObject({
      id,
      externalId: newExternalId,
      externalSource: MOCK_EXTERNAL_SOURCE,
    });

    if (!result.ok) {
      console.error('Error updating object:', result.val);
      return undefined;
    }

    return result.val;
  }

  async createImageDeliverable(
    imageSrc: string,
    frameId: UUID,
  ): Promise<CreatedImageDeliverable> {
    const imageId = generateUUID();
    const content = this.parseImageDataUrl(imageSrc);

    const metadata = await this.generateImageDeliverableInsights(
      imageSrc,
      content.mimeType,
    );

    const payload = this.buildImageDeliverableTextMetadata(
      imageId,
      frameId,
      content,
      metadata,
    );
    const deliverable = await this.deliverableClient.createDeliverable(payload);
    return {
      id: deliverable.id,
      name: deliverable.name,
      ...metadata,
    };
  }

  private async generateImageDeliverableInsights(
    imageSrc: string,
    mimeType: string,
  ): Promise<ImageDeliverableTextMetadata | undefined> {
    if (this.workflowClient === null) {
      return undefined;
    }
    const aiImage = await this.resolveAiImageInput(imageSrc, mimeType);
    if (aiImage === undefined) {
      return undefined;
    }
    return this.generateImageDeliverableMetadata(
      aiImage.imageSrc,
      aiImage.mimeType,
    );
  }

  private async resolveAiImageInput(
    imageSrc: string,
    mimeType: string,
  ): Promise<{ imageSrc: string; mimeType: string } | undefined> {
    if (isSupportedImageSummarizerMimeType(mimeType)) {
      return { imageSrc, mimeType };
    }
    if (mimeType !== 'image/svg+xml') {
      return undefined;
    }

    try {
      const svgFile = this.svgDataUrlToFile(imageSrc);
      const maxBytes = Math.floor((MAX_IMAGE_SUMMARIZER_BASE64_BYTES * 3) / 4);
      const pngDataUrl = await rasterizeSvgToPngDataUrl(svgFile, maxBytes);

      return { imageSrc: pngDataUrl, mimeType: 'image/png' };
    } catch (error) {
      console.error('Failed to rasterize SVG for AI image pipeline', error);
      return undefined;
    }
  }

  private svgDataUrlToFile(dataUrl: string): File {
    const { mimeType, codeBase64 } = this.parseImageDataUrl(dataUrl);
    const binary = atob(codeBase64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new File([bytes], 'pasted-image.svg', { type: mimeType });
  }

  private async generateImageDeliverableMetadata(
    imageSrc: string,
    mimeType: string,
  ): Promise<ImageDeliverableTextMetadata | undefined> {
    if (
      this.workflowClient === null ||
      !isSupportedImageSummarizerMimeType(mimeType)
    ) {
      return undefined;
    }

    try {
      const { payload } = await this.workflowClient.callAiWorkflow<
        ImageSummarizerInput,
        ImageSummarizerOutput
      >({
        endpointPath: IMAGE_SUMMARIZER_ENDPOINT_PATH,
        payload: {
          base64Image: imageSrc,
          mimeType,
        },
      });

      if (isWorkflowErrorPayload(payload)) {
        console.error('Failed to generate image metadata', payload.message);
        return undefined;
      }

      const parsed = imageSummarizerOutputSchema.safeParse(payload);
      if (!parsed.success) {
        console.error('Invalid image metadata response', parsed.error);
        return undefined;
      }

      return this.normalizeImageDeliverableMetadata(parsed.data);
    } catch (error) {
      console.error('Failed to generate image metadata', error);
      return undefined;
    }
  }

  async deleteImageDeliverables(imageIds: UUID[]): Promise<void> {
    if (imageIds.length === 0) {
      return;
    }
    const result = await this.deliverableClient.deleteDeliverables(imageIds);
    if (result.err) {
      throw new Error('Failed to delete image deliverables');
    }
  }

  private parseImageDataUrl(imageSrc: string): {
    mimeType: string;
    codeBase64: string;
  } {
    const match = imageSrc.match(
      /^data:(?<mimeType>[^;]+);base64,(?<codeBase64>.+)$/,
    );
    const groups = match?.groups;
    if (
      groups?.['mimeType'] === undefined ||
      groups?.['codeBase64'] === undefined
    ) {
      throw new Error('Invalid image data URL format');
    }
    return {
      mimeType: groups['mimeType'],
      codeBase64: groups['codeBase64'],
    };
  }

  private buildImageDeliverableTextMetadata(
    imageId: UUID,
    frameId: UUID,
    content: { mimeType: string; codeBase64: string },
    metadata?: ImageDeliverableTextMetadata | undefined,
  ): CreateDeliverablePayload {
    return {
      type: 'image',
      id: imageId,
      name: `image-${imageId}`,
      content: { codeBase64: content.codeBase64 },
      ...(metadata === undefined
        ? {}
        : { preview: this.buildImageDeliverablePreview(metadata) }),
      definition: 'IMAGE',
      createRelationships: {
        displayedOn: [
          {
            id: frameId,
            createRelationships: {
              displays: [{ id: imageId }],
            },
          },
        ],
      },
    };
  }

  private buildImageDeliverablePreview(
    metadata: ImageDeliverableTextMetadata,
  ): ImageDeliverablePreview {
    return {
      version: IMAGE_DELIVERABLE_PREVIEW_VERSION,
      payload: metadata,
    };
  }

  private normalizeImageDeliverableMetadata(
    metadata: z.infer<typeof storedImageDeliverableMetadataSchema>,
  ): ImageDeliverableTextMetadata {
    const description = metadata.description.trim();
    const alt = metadata.alt.trim();
    if (description.length === 0 || alt.length === 0) {
      throw new Error('Image description and alt text are required');
    }
    return {
      description,
      alt,
      ...toImageClassificationFields({
        classification: metadata.classification,
        mermaid: metadata.mermaid?.trim(),
      }),
    };
  }

  async deleteFrameImageRelationships(
    frameId: UUID,
    imageDeliverableIds: UUID[],
  ): Promise<void> {
    if (imageDeliverableIds.length === 0) return;
    const payload: DeleteDeliverableRelationshipPayload[] =
      imageDeliverableIds.map((imageId) => ({
        roleName: 'displayedOn',
        deliverableSourceId: imageId,
        deliverableTargetId: frameId,
      }));
    try {
      const result =
        await this.deliverableClient.deleteDeliverableRelationships(payload);
      if (result.err) {
        console.error('Failed to delete frame image relationships', result.val);
      }
    } catch (error) {
      console.error('Failed to delete frame image relationships', error);
    }
  }

  async createFrameImageRelationships(
    frameId: UUID,
    imageDeliverableIds: UUID[],
  ): Promise<void> {
    const uniqueImageDeliverableIds = [...new Set(imageDeliverableIds)];
    if (uniqueImageDeliverableIds.length === 0) return;
    const relationships: DeliverableRelationship[] =
      uniqueImageDeliverableIds.map((imageId) => ({
        role: 'displayedOn',
        deliverableSourceId: imageId,
        deliverableTargetId: frameId,
      }));
    await this.deliverableClient.createDeliverableRelationships(relationships);
  }

  getImageDeliverables(
    imageIds: string[],
    options?: GetImageDeliverablesOptions,
  ): Observable<ImageDeliverable[]> {
    if (imageIds.length === 0) {
      return of([]);
    }
    const deliverablesFilter: DeliverableFilter = {
      type: ['image'],
      id: imageIds,
    };
    const result = this.deliverableClient.getDeliverables(deliverablesFilter);
    return from(result.stream()).pipe(
      toArray(),
      switchMap(async (deliverables) => {
        const resolved: ImageDeliverable[] = [];
        for (const deliverable of deliverables) {
          let imageSrc: string | undefined;
          const savedMetadata =
            await this.resolveImageDeliverableMetadata(deliverable);
          if (!deliverable.content) {
            resolved.push({
              id: deliverable.id,
              imageSrc,
              ...savedMetadata,
            });
            continue;
          }
          const resolvedContent = await deliverable.content;
          if (
            typeof resolvedContent === 'object' &&
            'codeBase64' in resolvedContent &&
            typeof resolvedContent.codeBase64 === 'string'
          ) {
            const mimeType = detectMimeTypeFromBase64(
              resolvedContent.codeBase64,
            );
            imageSrc = `data:${mimeType};base64,${resolvedContent.codeBase64}`;
          }
          const resolvedMetadata =
            await this.resolveRequestedImageDeliverableMetadata(
              deliverable,
              imageSrc,
              savedMetadata,
              options,
            );
          resolved.push({
            id: deliverable.id,
            imageSrc,
            ...resolvedMetadata,
          });
        }
        return resolved;
      }),
      catchError((ogError) =>
        throwError(
          () =>
            new Error(
              `Failed to fetch image deliverables for ids: ${imageIds.join(', ')}`,
              { cause: ogError },
            ),
        ),
      ),
    );
  }

  private async resolveImageDeliverableMetadata(
    deliverable: Deliverable,
  ): Promise<ImageDeliverableTextMetadata | undefined> {
    const preview = deliverable.preview;
    if (preview === undefined) {
      return undefined;
    }
    try {
      return this.parseImageDeliverablePreview(await preview);
    } catch {
      return undefined;
    }
  }

  private async resolveRequestedImageDeliverableMetadata(
    deliverable: Deliverable,
    imageSrc: string | undefined,
    savedMetadata: ImageDeliverableTextMetadata | undefined,
    options?: GetImageDeliverablesOptions,
  ): Promise<ImageDeliverableTextMetadata | undefined> {
    if (imageSrc === undefined) {
      return savedMetadata;
    }
    if (options?.forceRegeneration !== true) {
      return savedMetadata;
    }
    let regeneratedMetadata: ImageDeliverableTextMetadata | undefined;
    try {
      const mimeType = this.parseImageDataUrl(imageSrc).mimeType;
      regeneratedMetadata = await this.generateImageDeliverableInsights(
        imageSrc,
        mimeType,
      );
    } catch (error) {
      console.error('Failed to regenerate image metadata', error);
      return savedMetadata;
    }
    if (regeneratedMetadata === undefined) {
      return savedMetadata;
    }
    const didPersist = await this.persistImageDeliverableMetadata(
      deliverable.id,
      regeneratedMetadata,
    );
    return didPersist ? regeneratedMetadata : savedMetadata;
  }

  private async persistImageDeliverableMetadata(
    deliverableId: string,
    metadata: ImageDeliverableTextMetadata,
  ): Promise<boolean> {
    if (!isUUID(deliverableId)) {
      return false;
    }
    try {
      await this.deliverableClient.updateDeliverable({
        id: deliverableId,
        preview: this.buildImageDeliverablePreview(metadata),
      });
      return true;
    } catch (error) {
      console.error('Failed to persist image metadata', error);
      return false;
    }
  }

  private parseImageDeliverablePreview(
    preview: unknown,
  ): ImageDeliverableTextMetadata | undefined {
    const parsedPreview = imageDeliverablePreviewSchema.safeParse(preview);
    if (!parsedPreview.success) {
      return undefined;
    }

    try {
      return this.normalizeImageDeliverableMetadata(parsedPreview.data.payload);
    } catch {
      return undefined;
    }
  }
}
